import { ContactIntegrationStatus } from "@/components/integrations/employee/employee-integrations-sync";
import { Button } from "@/components/ui/button";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { useTranslation } from "@/hooks/useTranslation";
import {
  ContactCrmIntegration,
  CrmIntegrationProviderSupported,
} from "@shaarp/common";
import { CloudCheckIcon, ExternalLink, RefreshCwIcon } from "lucide-react";

type SyncedIntegrationsProps = {
  integrations: ContactIntegrationStatus[];
  onSync: (integrationKey: CrmIntegrationProviderSupported) => void;
  onOpenExternal: (crmContact: ContactCrmIntegration) => void;
};

/**
 * Each row carries its own busy state, so refreshing one CRM leaves the other
 * row idle and clickable.
 *
 * Each row opens *its own* CRM record: the link comes from the integration it
 * belongs to, so with two CRMs connected one row can no longer send the user to
 * the other's record.
 */
export const SyncedIntegrations = ({
  integrations,
  onSync,
  onOpenExternal,
}: SyncedIntegrationsProps) => {
  const { t } = useTranslation();

  return (
    <>
      <div className="px-3 py-2">
        <div className="text-sm font-medium text-foreground">
          {t("employee.integrations.syncedIntegrations")}
        </div>
      </div>

      {integrations.map((integration) => (
        <DropdownMenuItem
          key={integration.key}
          disabled
          className="flex gap-4 items-center p-2"
        >
          <img
            src={integration.logo}
            alt={integration.name}
            className="object-contain shrink-0 w-6 h-6"
          />
          <div className="flex flex-col flex-1 gap-1">
            <span className="text-sm font-medium">{integration.name}</span>
            <div className="flex gap-2 items-center">
              {!integration.isLoading && (
                <CloudCheckIcon className="w-4 h-4 text-success" />
              )}
              <span className="text-xs text-muted-foreground">
                {integration.isLoading
                  ? t("employee.integrations.syncing")
                  : t("employee.integrations.synced")}
              </span>
            </div>
          </div>
          <div className="flex shrink-0 gap-2 items-center">
            <Button
              variant="ghost"
              size="sm"
              className="p-0 w-8 h-8 hover:bg-muted"
              disabled={!integration.crmRecord?.directLink}
              onClick={(e) => {
                e.stopPropagation();
                if (integration.crmRecord) {
                  onOpenExternal({
                    id: integration.crmRecord.id,
                    directLink: integration.crmRecord.directLink,
                  } as ContactCrmIntegration);
                }
              }}
              title={t("employee.integrations.openInIntegration", {
                integration: integration.name,
              })}
            >
              <ExternalLink className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="p-0 w-8 h-8 hover:bg-muted"
              disabled={integration.isLoading}
              onClick={(e) => {
                e.stopPropagation();
                onSync(integration.key);
              }}
              title={t("employee.integrations.syncToIntegration", {
                integration: integration.name,
              })}
            >
              <RefreshCwIcon
                className={`w-4 h-4 ${
                  integration.isLoading ? "animate-spin" : ""
                }`}
              />
            </Button>
          </div>
        </DropdownMenuItem>
      ))}
    </>
  );
};
